import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Tabs,
  Tab,
  Grid,
  Paper,
  Button,
  Typography,
  Divider,
  CardContent,
  useTheme,
  Chip,
  Stack,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PeopleIcon from "@mui/icons-material/People";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import HistoryToggleOffRoundedIcon from "@mui/icons-material/HistoryToggleOffRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import DescriptionIcon from "@mui/icons-material/Description";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import themeCreator from "@Themes/themeCreator";
import PageTitle from "@Components/PageTitle/PageTitle";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import { StyledCard, SectionTitle, ValueText } from "./styles";
import { TabPanel } from "./TabPanel";
import { MemberList } from "./MemberList";
import { ResponseDetails } from "./ResponseDetails";
import ExitRequestLogs from "@Components/ExitRequestLogs/ExitRequestLogs";
import { dummyData } from "./DummyData";
import dayjs from "dayjs";
import useGetItem from "@Hooks/useGetItem";
import { getStatusColor } from "@Utils/Validations/HelperFunctions/StyleFunction";
import { getRestrictionTypeColor, getRestrictionTypeColorStyles } from "../../../Utils/Validations/HelperFunctions/StyleFunction";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance from "@API/axios";
import CircularProgress from "@mui/material/CircularProgress";
import CustomSnackbar from "@Components/Snackbar/Snackbar";
import { setSnackbar } from "@Redux/snackBarSlice";
import qs from "qs";

const ExitRequestDetail = () => {
  const { reqId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isCanceled = location.pathname.includes("canceled-requests");
  const [requestData, setRequestData] = useState(null);
  const [basicInfoData, setBasicInfoData] = useState(null);
  const [targetUserWithResponses, setTargetUserWithResponses] = useState(null);

  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [selectedMember, setSelectedMember] = useState(null);
  const [responseSubTabValue, setResponseSubTabValue] = useState(0);
  const [isAdditionalCommentExpanded, setIsAdditionalCommentExpanded] = useState(false);

  const theme = useTheme();
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user.userId);
  const userRole = useSelector((state) => state.user.role);
  console.log("userId", userId);
  // Get navigation state
  const navigationState = location.state || {};
  const { openResponsesTab, selectedMemberId } = navigationState;
  const countriesByRegion = useMemo(() => {
    if (!basicInfoData?.countries?.length) return {};
    return basicInfoData.countries.reduce((acc, country) => {
      const regionName = country.regionName || "Unknown Region";
      if (!acc[regionName]) acc[regionName] = [];
      acc[regionName].push(country);
      return acc;
    }, {});
  }, [basicInfoData?.countries]);

  const exitRequestMeta = useMemo(
    () => ({
      ingredientName: basicInfoData?.ingredient?.name,
      casNumber: basicInfoData?.ingredient?.casNumber,
      status: basicInfoData?.status,
      userEmail: basicInfoData?.user?.email,
    }),
    [basicInfoData]
  );

  const handleBackClick = () => navigate(-1);
  const handleTabChange = (_, newValue) => setTabValue(newValue);

  const handleCloneClick = async () => {
    try {
      // Fetch clone data from API
      const response = await axiosInstance.get(`/exitRequest/clone/${reqId}`);

      if (response.data) {
        // Navigate to create page with cloned data in state
        navigate(DefaultRoutes.createIngredientExit, {
          state: {
            cloneData: response.data,
            clonedFrom: reqId
          }
        });
      }
    } catch (error) {
      console.error("Error fetching copy data:", error);
      dispatch(
        setSnackbar({
          open: true,
          message: "Failed to load exit request data for copying",
          error: true,
        })
      );
    }
  };

  const handleMemberSelect = (member) => {
    setSelectedMember(member);
    setResponseSubTabValue(0);
  };

  const handleResponseSubTabChange = (_, newValue) => {
    const hasExitPlanTab = selectedMember?.response?.isCompliant === false;
    const maxTabIndex = hasExitPlanTab ? 1 : 0;
    if (newValue <= maxTabIndex) {
      setResponseSubTabValue(newValue);
    }
  };

  // Calculate response statistics
  const calculateResponseStats = (members) => {
    if (!members || !members.length) return {};

    const total = members.length;
    const responded = members.filter((member) => member.hasResponded).length;
    const pending = total - responded;
    const compliant = members.filter(
      (member) => member.hasResponded && member.response.status === "compliant"
    ).length;
    const incompliant = members.filter(
      (member) =>
        member.hasResponded && member.response.status === "incompliant"
    ).length;
    const pendingExitPlans = members.filter(
      (member) =>
        member.hasResponded &&
        member.response.status === "incompliant" &&
        (!member.response.exitPlanStatus ||
          member.response.exitPlanStatus === "under review")
    ).length;
    const readyExitPlans = members.filter(
      (member) =>
        member.hasResponded &&
        member.response.status === "incompliant" &&
        member.response.exitPlanStatus === "ready"
    ).length;

    const responseRate = total > 0 ? (responded / total) * 100 : 0;

    return {
      total,
      responded,
      pending,
      compliant,
      incompliant,
      pendingExitPlans,
      readyExitPlans,
      responseRate,
    };
  };
  const getItem = useGetItem();

  const formateResponses = (basicInfo) => {
    console.log("Starting to format responses");
    const flattened = basicInfo.targetedMembers.flatMap((member) =>
      member.categories.map((category) => {
        const { categories, region, compliant, hasResponded, ...rest } = member;
        return {
          ...rest,
          compliant: null,
          hasResponded: null,
          category,
          region: member.region?.name || '',
        };
      })
    );
    console.log("Flattened members:", flattened);

    // Filter out DRAFT responses before mapping to members
    const nonDraftResponses = basicInfo.responses.filter(
      (response) => response.status !== "DRAFT"
    );

    nonDraftResponses.forEach((response) => {
      const targetUser = flattened.find(
        (member) =>
          member.id === response.submittedBy.id &&
          member.category === response.submittedBy.categories[0]?.name
      );
      if (targetUser) {
        targetUser.hasResponded = true;
        targetUser.compliant = response.isCompliant;
        targetUser.response = response;
      }
    });
    setTargetUserWithResponses(flattened);
  };

  useEffect(() => {
    const promises = [getItem("exitRequest/detail", reqId)];
    Promise.all(promises)
      .then(([basicInfo]) => {
        console.log("heeeeeeeee",basicInfo);
        setBasicInfoData(basicInfo);
        formateResponses(basicInfo);

        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  }, []);

  // Handle reload state from query params
  useEffect(() => {
    const params = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });

    console.log("Query params on reload:", params);
    console.log("targetUserWithResponses available:", !!targetUserWithResponses);

    // Only proceed if we have the data loaded
    if (!targetUserWithResponses || targetUserWithResponses.length === 0) {
      return;
    }

    // Restore tab state
    if (params.tab === "responses") {
      console.log("Setting tab to responses");
      setTabValue(1);
    }

    // Restore selected member
    if (params.selectedMemberId) {
      const member = targetUserWithResponses.find(
        (m) => m.id == params.selectedMemberId
      );
      console.log("Found member for restoration:", member);
      if (member) {
        setSelectedMember(member);
      }
    }

    // Restore exit plan tab
    if (params.showExitPlan === "true") {
      console.log("Setting response sub tab to exit plan");
      setResponseSubTabValue(2);
    }

    // Clean up query params after state restoration
    if (params.tab || params.selectedMemberId || params.showExitPlan) {
      // Remove the params so they don't interfere with normal navigation
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [targetUserWithResponses, dispatch]);

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <LoadingComponent size="5em" thickness={5} color="primary" />
      </Box>
    );
  // if (!requestData)
  //   return (
  //     <Typography variant="h6" align="center" color="error" py={5}>
  //       Request not found
  //     </Typography>
  //   );
  const formatLabel = (input) => {
    return input?.replace(/_/g, " "); // Replace underscores with spaces
  };
  // const responseStats = calculateResponseStats(requestData.targetedMembers);

  // Content to display based on selected tab
  const renderContent = () => {
    if (tabValue === 0) {
      // Basic Information Tab Content
      return (
        <Box sx={{ overflow: "auto", height: "100%", pr: 1, pt: 0 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6} sx={{ mt: 0.5 }}>
              <Grid container spacing={1} direction="column">
                {/* Ingredient Details */}
                <Grid item sx={{ minHeight: "120px", height: "auto" }}>
                  <StyledCard
                    sx={{
                      transition: "0.3s",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      },
                      backgroundColor: "#fcfcfc",
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    <CardContent sx={{ p: 0.75, pb: 0.75 }}>
                      <SectionTitle
                        variant="h6"
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: "1rem",
                          borderBottomWidth: "2px",
                          borderBottomColor: "rgba(15, 66, 170, 0.2)",
                          pb: 0.5,
                          mb: 0.5,
                        }}
                      >
                        Ingredient Details
                      </SectionTitle>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              mb: 0.1,
                            }}
                          >
                            Name
                          </Typography>
                          <ValueText
                            variant="body1"
                            sx={{
                              fontSize: "0.9rem",
                              fontWeight: 600,
                              color: theme.palette.primary.main,
                              overflowWrap: "break-word",
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                              maxWidth: "100%",
                              display: "block",
                            }}
                          >
                            {basicInfoData?.ingredient?.name}
                          </ValueText>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              mb: 0.1,
                            }}
                          >
                            CAS Number
                          </Typography>
                          <ValueText
                            variant="body1"
                            sx={{
                              fontSize: "0.9rem",
                              fontFamily: "monospace",
                              letterSpacing: "0.05em",
                              bgcolor: "white",
                              py: 0.25,
                              px: 1,
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                              display: "inline-block",
                              overflowWrap: "break-word",
                              wordBreak: "break-word",
                            }}
                          >
                            {basicInfoData?.ingredient?.casNumber}
                          </ValueText>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              mb: 0.1,
                            }}
                          >
                            Description
                          </Typography>
                          <ValueText
                            variant="body1"
                            sx={{
                              fontSize: "0.9rem",
                              color: theme.palette.black.light2,
                              bgcolor: "rgba(255, 255, 255, 0.7)",
                              p: 0.35,
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                              lineHeight: 1.3,
                              // Allow the description area to grow vertically for long content
                              maxHeight: "none",
                              overflow: "visible",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              width: "100%",
                            }}
                          >
                            {basicInfoData?.ingredient?.description}
                          </ValueText>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid>

                {/* User Information */}
                <Grid item sx={{ minHeight: "120px", height: "auto" }}>
                  <StyledCard
                    sx={{
                      transition: "0.3s",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                      },
                      backgroundColor: "#fcfcfc",
                    }}
                  >
                    <CardContent sx={{ p: 0.9, pb: 0.9 }}>
                      <SectionTitle
                        variant="h6"
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: "1rem",
                          borderBottomWidth: "2px",
                          borderBottomColor: theme.palette.grey.light2,
                          pb: 0.5,
                          mb: 0.5,
                        }}
                      >
                        Request Created By
                      </SectionTitle>
                      <Grid container spacing={1} alignItems="stretch">
                        <Grid item xs={12} sm={4}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              mb: 0.1,
                            }}
                          >
                            Username
                          </Typography>
                          <ValueText
                            variant="body1"
                            sx={{
                              fontSize: "0.9rem",
                              color: theme.palette.black.light,
                            }}
                          >
                            {basicInfoData?.user?.username}
                          </ValueText>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              mb: 0.1,
                            }}
                          >
                            Role
                          </Typography>
                          <ValueText
                            variant="body1"
                            sx={{
                              fontSize: "0.9rem",
                              color: theme.palette.primary.main,
                              bgcolor: theme.palette.blue.light,
                              py: 0.25,
                              px: 1.25,
                              borderRadius: 5,
                              display: "inline-block",
                            }}
                          >
                            {basicInfoData?.user?.role}
                          </ValueText>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={4}
                          style={{ transform: "translateX(12%)" }}
                        >
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              mb: 0.1,
                            }}
                          >
                            Email
                          </Typography>
                          <ValueText
                            variant="body1"
                            sx={{
                              fontSize: "0.9rem",
                              color: theme.palette.blue.main,
                              textDecoration: "underline",
                              textDecorationColor: "rgba(21, 155, 202, 0.3)",
                            }}
                          >
                            {basicInfoData?.user?.email}
                          </ValueText>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid>

                {/* Approval Managers + Restrictions Descriptions column */}
                    <Grid item>
                      <Grid container spacing={1} direction="column">
                    <Grid item>
                      <StyledCard
                        sx={{
                          transition: "0.3s",
                          "&:hover": {
                            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                            borderLeft: `4px solid ${theme.palette.primary.main}`,
                          },
                          backgroundColor: "#fcfcfc",
                        }}
                      >
                        <CardContent sx={{ p: 0.9, pb: 0.9 }}>
                          <SectionTitle
                            variant="h6"
                            sx={{
                              color: theme.palette.primary.main,
                              fontSize: "1rem",
                              borderBottomWidth: "2px",
                              borderBottomColor: theme.palette.grey.light2,
                              pb: 0.5,
                              mb: 0.5,
                            }}
                          >
                            Approval List
                          </SectionTitle>

                          <Box
                            sx={{
                              maxHeight: "40vh",
                              overflowY: "auto",
                              pr: 1,
                            }}
                          >
                            <Grid container spacing={1}>
                              {basicInfoData?.approvingManagers?.map(
                                (manager, index) => (
                                  <React.Fragment key={index}>
                                    <Grid item xs={12} sm={6}>
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{
                                          fontSize: "0.75rem",
                                          fontWeight: 500,
                                          mb: 0.1,
                                        }}
                                      >
                                        User Name
                                      </Typography>
                                      <ValueText
                                        variant="body1"
                                        sx={{
                                          fontSize: "0.9rem",
                                          color: theme.palette.black.light,
                                        }}
                                      >
                                        {manager.username}
                                      </ValueText>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{
                                          fontSize: "0.75rem",
                                          fontWeight: 500,
                                          mb: 0.1,
                                        }}
                                      >
                                        Email
                                      </Typography>
                                      <ValueText
                                        variant="body1"
                                        sx={{
                                          fontSize: "0.9rem",
                                          color: theme.palette.blue.main,
                                          textDecoration: "underline",
                                          textDecorationColor:
                                            "rgba(21, 155, 202, 0.3)",
                                        }}
                                      >
                                        {manager.email}
                                      </ValueText>
                                    </Grid>
                                  </React.Fragment>
                                )
                              )}
                            </Grid>
                          </Box>
                        </CardContent>
                      </StyledCard>
                    </Grid>

                    {/* Restrictions Descriptions */}
                    {(() => {
                      const uniqueCategories = [];
                      const seen = new Set();
                      (basicInfoData?.targetedMembers || []).forEach((member) => {
                        (member.categoryAssignments || []).forEach((ca) => {
                          if (ca.categoryDescription) {
                            seen.add(ca.categoryName);
                            uniqueCategories.push({regionName: member.region?.name || "N/A", categoryName: ca.categoryName, categoryDescription: ca.categoryDescription});
                          }
                        });
                      });
                      if (uniqueCategories.length === 0) return null;
                      return (
                        <Grid item>
                          <StyledCard
                            sx={{
                              transition: "0.3s",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                                borderLeft: `4px solid ${theme.palette.primary.main}`,
                              },
                              backgroundColor: "#fcfcfc",
                            }}
                          >
                            <CardContent sx={{ p: 0.9, pb: 0.9 }}>
                              <SectionTitle
                                variant="h6"
                                sx={{
                                  color: theme.palette.primary.main,
                                  fontSize: "1rem",
                                  borderBottomWidth: "2px",
                                  borderBottomColor: theme.palette.grey.light2,
                                  pb: 0.5,
                                  mb: 0.5,
                                }}
                              >
                                Restrictions Descriptions
                              </SectionTitle>
                              <Grid container spacing={1}>
                                {uniqueCategories.map((ca, i) => (
                                  <Grid item xs={12} sm={6} md={4} key={i}>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.1 }}
                                    >
                                      {ca.categoryName} - {ca.regionName}
                                    </Typography>
                                    <ValueText
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.8rem",
                                        color: theme.palette.black.light2,
                                        bgcolor: "rgba(255,255,255,0.7)",
                                        p: 0.5,
                                        borderRadius: 1,
                                        border: "1px solid #e0e0e0",
                                        lineHeight: 1.3,
                                        display: "block",
                                        maxHeight: "70px",
                                        overflow: "auto",
                                      }}
                                    >
                                      {ca.categoryDescription}
                                    </ValueText>
                                  </Grid>
                                ))}
                              </Grid>
                            </CardContent>
                          </StyledCard>
                        </Grid>
                      );
                    })()}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Request Info */}
            <Grid item xs={12} md={6} sx={{ mt: 1 }}>
              <StyledCard
                sx={{
                  transition: "0.3s",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                  },
                  backgroundColor: "#fcfcfc",
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <SectionTitle
                    variant="h6"
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: "1rem",
                      borderBottomWidth: "2px",
                      borderBottomColor: theme.palette.grey.light2,
                      pb: 0.5,
                      mb: 1.5,
                    }}
                  >
                    Request Information
                  </SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          mb: 0.25,
                        }}
                      >
                        Created At
                      </Typography>
                      <ValueText
                        variant="body1"
                        sx={{
                          fontSize: "0.95rem",
                          color: theme.palette.blue.main,
                          textDecoration: "underline",
                          textDecorationColor: "rgba(21, 155, 202, 0.3)",
                        }}
                      >
                        {dayjs(Number(basicInfoData?.createdAt)).format(
                          "MM/DD/YYYY"
                        )}
                      </ValueText>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 500,
                            mb: 0.25,
                          }}
                        >
                            {basicInfoData?.primaryDateType
                              ? basicInfoData.primaryDateType.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
                              : "Primary Date"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Format: MM/DD/YYYY
                        </Typography>
                      </Box>

                      <ValueText
                        variant="body1"
                        sx={{
                          fontSize: "0.95rem",
                          color: theme.palette.red.main,
                        }}
                      >
                        {dayjs(Number(basicInfoData?.primaryDate)).format(
                          "MM/DD/YYYY"
                        )}
                      </ValueText>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 500,
                            mb: 0.25,
                          }}
                        >
                          {basicInfoData?.secondaryDateType
                            ? basicInfoData.secondaryDateType.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
                            : "Secondary Date"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Format: MM/DD/YYYY
                        </Typography>
                      </Box>
                      <ValueText
                        variant="body1"
                        sx={{
                          fontSize: "0.95rem",
                          color: theme.palette.warning.main,
                        }}
                      >
                        {basicInfoData?.secondaryDate
                          ? dayjs(Number(basicInfoData?.secondaryDate)).format("MM/DD/YYYY")
                          : "-"}
                      </ValueText>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 500,
                            mb: 0.25,
                          }}
                        >
                          UL Internal Date
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Format: MM/DD/YYYY
                        </Typography>
                      </Box>
                      <ValueText
                        variant="body1"
                        sx={{
                          fontSize: "0.95rem",
                          color: theme.palette.info.main,
                        }}
                      >
                        {basicInfoData?.ulInternalDate
                          ? dayjs(Number(basicInfoData?.ulInternalDate)).format("MM/DD/YYYY")
                          : "-"}
                      </ValueText>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          mb: 0.25,
                        }}
                      >
                        status
                      </Typography>
                      <ValueText
                        variant="body1"
                        sx={{
                          fontSize: "0.8rem",
                          color: getStatusColor(basicInfoData?.status),
                          bgcolor: theme.palette.blue.light,
                          py: 0.25,
                          px: 1.25,
                          borderRadius: 5,
                          display: "inline-block",
                        }}
                      >
                        {formatLabel(basicInfoData?.status)}
                      </ValueText>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          mb: 0.25,
                        }}
                      >
                        Restriction Type
                      </Typography>
                      <ValueText
                        variant="body1"
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          border: "1px solid #E0E0E0",
                          px: 1, // equivalent to padding: "4px 8px"
                          py: 0.5,
                          borderRadius: "16px",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        {basicInfoData?.restrictionType}
                      </ValueText>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          mb: 0.25,
                        }}
                      >
                        Impact Assessments Remaining
                      </Typography>
                      <ValueText
                        variant="body1"
                        sx={{
                          fontSize: "0.95rem",
                          color: theme.palette.primary.main,
                          bgcolor: theme.palette.blue.light,
                          py: 0.25,
                          px: 1.25,
                          borderRadius: 5,
                          display: "inline-block",
                        }}
                      >
                        {basicInfoData?.numberOfRemainingResponses}
                      </ValueText>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          mb: 0.25,
                        }}
                      >
                        Request Type
                      </Typography>
                      <ValueText
                        variant="body1"
                        sx={{
                          fontSize: "0.8rem",
                          color: basicInfoData?.isUiExitRequest
                            ? theme.palette.primary.main
                            : theme.palette.grey.medium,
                          bgcolor: basicInfoData?.isUiExitRequest
                            ? theme.palette.blue.light
                            : theme.palette.grey.light,
                          py: 0.25,
                          px: 1.25,
                          borderRadius: 5,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {basicInfoData?.isUiExitRequest ? (
                          <SmartphoneIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <DescriptionIcon sx={{ fontSize: 16 }} />
                        )}
                        {basicInfoData?.isUiExitRequest
                          ? "UI Request"
                          : "Normal Request"}
                      </ValueText>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          mb: 0.25,
                        }}
                      >
                        Additional comment
                      </Typography>
                      <Box
                        sx={{
                          position: "relative",
                          bgcolor: "rgba(255, 255, 255, 0.85)",
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          p: 1,
                          maxHeight: isAdditionalCommentExpanded ? "220px" : "88px",
                          overflowY: "auto",
                          overflowX: "hidden",
                          transition: "max-height 0.2s ease",
                        }}
                      >
                        <ValueText
                          variant="body1"
                          sx={{
                            fontSize: "0.92rem",
                            color: theme.palette.black.light2,
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            pr: 0.5,
                          }}
                        >
                          {basicInfoData?.additionalInfo ||
                            "No additional comment"}
                        </ValueText>
                      </Box>
                      {(basicInfoData?.additionalInfo || "").length > 140 && (
                        <Button
                          size="small"
                          onClick={() =>
                            setIsAdditionalCommentExpanded((prev) => !prev)
                          }
                          sx={{
                            mt: 0.5,
                            px: 0,
                            minWidth: "auto",
                            textTransform: "none",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}
                        >
                          {isAdditionalCommentExpanded ? "Show less" : "Show more"}
                        </Button>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          mb: 0.5,
                        }}
                      >
                        Regions & Countries
                      </Typography>
                      {Object.keys(countriesByRegion).length ? (
                        <Stack spacing={1}>
                          {Object.entries(countriesByRegion).map(
                            ([regionName, countries]) => (
                              <Box key={regionName}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                    mb: 0.5,
                                  }}
                                >
                                  {regionName}
                                </Typography>
                                <Stack
                                  direction="row"
                                  spacing={0.75}
                                  useFlexGap
                                  flexWrap="wrap"
                                >
                                  {countries.map((country) => (
                                    <Chip
                                      key={country.id || country.countryCode}
                                      label={country.name}
                                      size="small"
                                      sx={{ fontSize: "0.75rem" }}
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            )
                          )}
                        </Stack>
                      ) : (
                        <ValueText
                          variant="body1"
                          sx={{
                            fontSize: "0.9rem",
                            color: theme.palette.black.light2,
                          }}
                        >
                          No countries have been assigned
                        </ValueText>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>

          </Grid>
        </Box>
      );
    } else if (!isCanceled && tabValue == 1) {
      return (
        <Grid container spacing={2} sx={{ height: "100%" }}>
          {/* Members List */}
          <Grid item xs={12} md={5} lg={3.3} sx={{ height: "100%" }}>
            <Paper variant="outlined" sx={{ height: "100%", overflow: "auto" }}>
              <MemberList
                members={targetUserWithResponses}
                selectedMember={selectedMember}
                onSelect={handleMemberSelect}
              />
            </Paper>
          </Grid>

          {/* Response Details */}
          <Grid item xs={12} md={7} lg={8.7} sx={{ height: "100%" }}>
            <Paper
              variant="outlined"
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <ResponseDetails
                member={selectedMember}
                responseSubTabValue={responseSubTabValue}
                onSubTabChange={handleResponseSubTabChange}
                approvingManagers={basicInfoData?.approvingManagers || []}
              />
            </Paper>
          </Grid>
        </Grid>
      );
    } else {
      return (
        <Box sx={{ height: "100%", overflow: "auto", pr: 1, pt: 1 }}>
          <ExitRequestLogs
            exitRequestId={reqId}
            meta={exitRequestMeta}
            height="65vh"
            minHeight="360px"
            variant="tab"
            showSummary={false}
          />
        </Box>
      );
    }
  };

  return (
    <Box
      sx={{
        pb: 0,
        pt: 3,
        pr: 2,
        pl: 2,
        height: "91vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <PageTitle
          title="Exit Request Info"
          PageIcon={<ExitToAppIcon color="white" fontSize="large" />}
          iconColor={themeCreator().palette.primary.main}
          noMargin
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          {userRole === "REGULATORY" && (
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCloneClick}
              sx={{ textTransform: "none" }}
              color="primary"
            >
              Copy
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            sx={{ textTransform: "none" }}
          >
            Return
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="exit request details tabs"
          sx={{
            minHeight: "40px", // Reduced height
            "& .MuiTab-root": {
              minHeight: "40px", // Reduced height
              py: 0.5, // Reduced padding
              fontSize: "0.85rem", // Smaller font size
            },
          }}
        >
          <Tab
            icon={<AssignmentIcon />}
            iconPosition="start"
            label="Regulatory Information"
            id="exit-request-tab-0"
          />
          {!isCanceled && (
            <Tab
              icon={<PeopleIcon />}
              iconPosition="start"
              label="R&D Impact Assessments"
              id="exit-request-tab-1"
            />
          )}
          <Tab
            icon={<HistoryToggleOffRoundedIcon />}
            iconPosition="start"
            label="Logs"
            id="exit-request-tab-logs"
          />
          {/* <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label="Impact Assessments Summary"
            id="exit-request-tab-2"
          /> */}
        </Tabs>
      </Box>

      {/* Content container - Full height */}
      <Box sx={{ flexGrow: 1, overflow: "hidden" }}>{renderContent()}</Box>

      {/* Global Snackbar */}
      <CustomSnackbar />
    </Box>
  );
};

export default ExitRequestDetail;
